import { PencilLine } from 'lucide-react';
import { AuditStamp } from '../../../components/ui/AuditStamp';
import { vehicleCallSign } from '../../../core/military-id';
import {
  VEHICLE_LOCATION_ICONS,
  VEHICLE_LOCATION_STYLES,
  describeVehicleLocation,
} from '../../../core/vehicle-location';
import { VEHICLE_CATEGORY_LABELS } from '../../../types/fleet';
import type { Vehicle, VehicleCategory, VehicleStatus } from '../../../types/fleet';

/** Operational colour for the status indicator, one entry per status. */
export const VEHICLE_STATUS_STYLES: Record<VehicleStatus, { dot: string; chip: string }> = {
  available: { dot: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  in_use: { dot: 'bg-brand-500', chip: 'bg-brand-50 text-brand-700 border-brand-100' },
  maintenance: { dot: 'bg-amber-500', chip: 'bg-amber-50 text-amber-700 border-amber-200' },
  grounded: { dot: 'bg-rose-500', chip: 'bg-rose-50 text-rose-700 border-rose-200' },
};

/** ירם is the operational pool, חום the administrative one. */
export const VEHICLE_CATEGORY_STYLES: Record<VehicleCategory, string> = {
  yaram: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  brown: 'bg-amber-50 text-amber-900 border-amber-200',
};

interface VehicleCardProps {
  vehicle: Vehicle;
  /**
   * The vehicle is bound to a convoy on an active task. Derived from the board
   * rather than stored — see `src/core/effective-status.ts`.
   */
  onMission?: boolean;
  /** The search resolved to this vehicle's three-digit number. */
  highlighted?: boolean;
  /** Evaluation moment for the audit line; injected so it can be frozen. */
  now?: number;
  onOpen: (vehicle: Vehicle) => void;
  /** Straight into the vehicle form. Omitted without `MANAGE_VEHICLES`. */
  onEdit?: (vehicle: Vehicle) => void;
  /**
   * Tapping the location badge opens the company sheet. Omitted without
   * `MANAGE_VEHICLES`, and the badge is then a plain label again.
   */
  onQuickStatus?: (vehicle: Vehicle) => void;
}

/**
 * One vehicle in the roster, kept deliberately short: the number it is called
 * by, its name and pool, its מספר לוחית רישוי, where it is, and the way in.
 *
 * Everything else the unit holds on it — the full type and model, the licence
 * weight class, the exact holding company, the drivers it opens, the loan
 * history and the delete control — lives behind "כרטיס רכב" in
 * {@link VehicleDetailsModal}, and the two transfers with them. That is what
 * lets a screen hold the whole pool at once instead of four trucks and a
 * scrollbar.
 *
 * The plate is the one field printed loud: it is what a driver reads back over
 * the net and what a form asks for, so it is set in a bold, high-contrast,
 * left-to-right line rather than the grey caption it used to be.
 *
 * The location badge is the row's one management control: a tap opens the
 * company sheet, so moving a vehicle between companies never means opening the
 * full card first.
 */
export function VehicleCard({
  vehicle,
  onMission = false,
  highlighted = false,
  now = Date.now(),
  onOpen,
  onEdit,
  onQuickStatus,
}: VehicleCardProps) {
  const location = describeVehicleLocation(vehicle);
  const locationStyle = VEHICLE_LOCATION_STYLES[location.kind];
  // The internal number when the unit has recorded one, otherwise the tail of
  // the מספר צ — which is what the vehicle is called by until they do.
  const badgeNumber = vehicleCallSign(vehicle);
  const badgeClass = `mt-0.5 inline-flex max-w-full items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-lg border ${locationStyle.chip}`;

  return (
    <article
      data-vehicle-card
      className={[
        'w-full bg-panel border rounded-2xl px-3 py-2.5 shadow-sm flex items-center gap-3',
        highlighted ? 'border-brand-500 ring-2 ring-brand-100' : 'border-line',
      ].join(' ')}
    >
      <div
        data-dir="ltr"
        data-highlighted={highlighted ? 'true' : undefined}
        className={[
          'shrink-0 w-11 h-11 rounded-xl flex items-center justify-center',
          'text-lg font-black tabular-nums tracking-tight',
          highlighted ? 'bg-brand-500 text-white' : 'bg-brand-50 text-brand-700',
        ].join(' ')}
        title={vehicle.militaryId ? 'מספר פנימי' : 'מספר צ — טרם נרשם מספר פנימי'}
      >
        {badgeNumber}
      </div>

      <div className="min-w-0 grow">
        <div className="flex items-center gap-1.5">
          <h3 className="font-bold text-sm truncate">{vehicle.name || vehicle.model}</h3>
          <span
            className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded-full border ${VEHICLE_CATEGORY_STYLES[vehicle.category]}`}
          >
            {VEHICLE_CATEGORY_LABELS[vehicle.category]}
          </span>
          {/* Out on an active task. Nobody set this — it is read off the convoy,
              and it clears itself the moment the task is closed. */}
          {onMission ? (
            <span
              data-on-mission="true"
              title="הרכב משובץ למשימה פעילה"
              className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded-full border ${VEHICLE_STATUS_STYLES.in_use.chip}`}
            >
              🚚 במשימה
            </span>
          ) : null}
        </div>

        {/* The plate, loud — the number the vehicle is identified by off the net. */}
        <p
          data-plate
          data-dir="ltr"
          title="מספר לוחית רישוי (מספר צ מלא)"
          className="text-base font-black tabular-nums tracking-wide text-ink"
        >
          {vehicle.plateNumber}
        </p>

        {/* Where the vehicle is — the one line the dispatcher reads first, and
            the way to change it. */}
        {onQuickStatus ? (
          <button
            type="button"
            data-location={location.kind}
            data-quick-status
            onClick={() => onQuickStatus(vehicle)}
            title="שינוי מיקום ושיוך פלוגה"
            aria-label={`שינוי שיוך — ${vehicle.name || vehicle.model}, ${location.label}`}
            className={`${badgeClass} hover:brightness-95 active:brightness-90 cursor-pointer`}
          >
            <span aria-hidden>{VEHICLE_LOCATION_ICONS[location.kind]}</span>
            <span className="truncate">{location.label}</span>
            <span aria-hidden className="text-[9px] opacity-60">
              ▾
            </span>
          </button>
        ) : (
          <span data-location={location.kind} className={badgeClass}>
            <span aria-hidden>{VEHICLE_LOCATION_ICONS[location.kind]}</span>
            <span className="truncate">{location.label}</span>
          </span>
        )}

        {/* "מי עידכן מה" — the commander who last moved this vehicle's status or
            handed it over, under the badge that says where it is. */}
        <AuditStamp record={vehicle} now={now} className="mt-0.5" />
      </div>

      <div className="shrink-0 flex flex-col gap-1.5">
        <button
          type="button"
          onClick={() => onOpen(vehicle)}
          className="h-8 px-3 rounded-xl border border-brand-500 bg-brand-500 text-white text-xs font-medium hover:bg-brand-700"
        >
          כרטיס רכב
        </button>
        {onEdit ? (
          <button
            type="button"
            onClick={() => onEdit(vehicle)}
            title="ערוך רכב"
            className="h-8 px-3 rounded-xl border border-line text-xs text-ink/70 hover:text-ink flex items-center justify-center gap-1"
          >
            <PencilLine size={13} aria-hidden />
            עריכה
          </button>
        ) : null}
      </div>
    </article>
  );
}
